// HEADER-DOCS-012: DocumentCard
// Spec: §4.3, §5.2, §16.17 (file-type icons)

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Box, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import {
  documentsApi,
  DocumentDto,
  DOC_TYPE_LABELS,
  httpStatusOf,
} from '../../../../services/camMirrorService';
import { DeleteConfirmModal } from '../../../../components/studentActionBar/DeleteConfirmModal';
import { triggerDownload } from '../../../../utils/triggerDownload';
import { canModifyDoc, ViewerRole } from './documentPermissions';

interface DocumentCardProps {
  doc: DocumentDto;
  viewerRole: ViewerRole;
  currentUserId: number | null;
  onEdit: () => void;
  onDeleted: () => void;
}

export const DocumentCard: React.FC<DocumentCardProps> = ({
  doc,
  viewerRole,
  currentUserId,
  onEdit,
  onDeleted,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const isLink = doc.origin === 'link';
  const canModify = canModifyDoc(doc, viewerRole, currentUserId);

  const onDownload = async () => {
    try {
      // Always request a fresh presigned URL: the one embedded in the list response
      // has a 5-minute TTL and is usually expired (S3 AccessDenied) by the time the user clicks.
      const url = (await documentsApi.getDownloadUrl(doc.externalId)).downloadUrl;
      // HEADER-DOCS-026 follow-up: download in place instead of opening an about:blank tab.
      triggerDownload(url, doc.displayName);
    } catch (err) {
      if (httpStatusOf(err) === 410) {
        // §4.3: file gone from storage, drop the card from the list.
        toast.error('This file is no longer available.');
        onDeleted();
      } else {
        toast.error("We couldn't open the file. Please try again.");
      }
    }
  };

  const onOpenLink = () => {
    if (doc.linkUrl) window.open(doc.linkUrl, '_blank', 'noopener,noreferrer');
  };

  // Close the confirm dialog only on success; on failure it stays open and shows its own error.
  const onDelete = async () => {
    await documentsApi.softDelete(doc.externalId);
    setConfirmingDelete(false);
    onDeleted();
  };

  const Icon = pickIcon(doc);
  const tone = pickTone(doc);

  return (
    <>
      <Box
        sx={{
          p: 2,
          borderRadius: 'var(--gm-radius-surface, 16px)',
          background: 'var(--gm-surface, #FFFFFF)',
          border: '1px solid var(--gm-hairline, #dde7f0)',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          height: '100%',
          transition: 'border-color 160ms ease, background-color 160ms ease',
          '&:hover': { borderColor: 'var(--gm-hairline-strong, #c6d6e4)' },
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box
            aria-hidden
            sx={{
              width: 40,
              height: 40,
              borderRadius: 'var(--gm-radius-nested, 10px)',
              display: 'grid',
              placeItems: 'center',
              background: tone.bg,
              color: tone.fg,
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          {canModify && (
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              aria-label="Document actions"
              aria-haspopup="menu"
              aria-expanded={menuAnchor != null}
              sx={iconBtnSx}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <Typography
          component="h2"
          sx={{
            m: 0,
            fontSize: 'var(--gm-text-md, 14px)',
            fontWeight: 600,
            lineHeight: 1.35,
            color: 'var(--gm-ink, #121d31)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
          title={doc.displayName}
        >
          {doc.displayName}
        </Typography>
        <Typography sx={{ m: 0, fontSize: 'var(--gm-text-sm, 12.5px)', color: 'var(--gm-ink-muted, #596b8b)', lineHeight: 1.4 }}>
          {metaLine(doc)}
        </Typography>
        <Box sx={{ mt: 'auto', pt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 'var(--gm-text-xs, 11.5px)', color: 'var(--gm-ink-muted, #596b8b)' }}>
            {new Date(doc.createdAt).toLocaleDateString()}
          </Typography>
          {isLink ? (
            <IconButton size="small" onClick={onOpenLink} aria-label="Open link" sx={{ ...iconBtnSx, ml: 'auto' }}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              onClick={onDownload}
              disabled={!doc.attachment}
              aria-label="Download"
              sx={{ ...iconBtnSx, ml: 'auto' }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Menu anchorEl={menuAnchor} open={menuAnchor != null} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onEdit();
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setConfirmingDelete(true);
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      {confirmingDelete && (
        <DeleteConfirmModal
          itemName={`"${doc.displayName}"`}
          onConfirm={onDelete}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}
    </>
  );
};

// Spec §16.17: file-type icon mapping (MIME first, file extension as fallback).
const iconBtnSx = {
  width: 44,
  height: 44,
  color: 'var(--gm-ink-strong, #324569)',
  '&:focus-visible': {
    outline: 'none',
    boxShadow: '0 0 0 3px var(--gm-accent-tint, #d6e4fd)',
  },
};

function metaLine(doc: DocumentDto): string {
  const type = DOC_TYPE_LABELS[doc.docType] ?? doc.docType;
  if (doc.origin === 'chat') return `${type} · From chat`;
  if (doc.origin === 'link') return `${type} · ${doc.linkSourceLabel || 'Linked'}`;
  return `${type} · Uploaded`;
}

function pickTone(doc: DocumentDto): { bg: string; fg: string } {
  if (doc.origin === 'link') {
    return { bg: 'var(--gm-doc-link-bg)', fg: 'var(--gm-doc-link-fg)' };
  }
  const mime = (doc.attachment?.mimeType ?? '').toLowerCase();
  const name = doc.displayName.toLowerCase();
  if (mime === 'application/pdf' || name.endsWith('.pdf')) {
    return { bg: 'var(--gm-doc-pdf-bg)', fg: 'var(--gm-doc-pdf-fg)' };
  }
  if (
    mime.includes('wordprocessingml') ||
    mime === 'application/msword' ||
    mime === 'application/vnd.oasis.opendocument.text' ||
    /\.(docx?|odt)$/.test(name)
  ) {
    return { bg: 'var(--gm-doc-docx-bg)', fg: 'var(--gm-doc-docx-fg)' };
  }
  if (
    mime.includes('spreadsheetml') ||
    mime === 'application/vnd.ms-excel' ||
    mime === 'application/vnd.oasis.opendocument.spreadsheet' ||
    mime === 'text/csv' ||
    /\.(xlsx?|ods|csv)$/.test(name)
  ) {
    return { bg: 'var(--gm-doc-xlsx-bg)', fg: 'var(--gm-doc-xlsx-fg)' };
  }
  if (mime.startsWith('image/') || /\.(png|jpe?g|heic|heif|webp)$/.test(name)) {
    return { bg: 'var(--gm-doc-img-bg)', fg: 'var(--gm-doc-img-fg)' };
  }
  return { bg: 'var(--gm-sunken, #e8f1f7)', fg: 'var(--gm-ink-muted, #596b8b)' };
}

function pickIcon(doc: DocumentDto): typeof InsertDriveFileIcon {
  if (doc.origin === 'link') return LinkIcon;
  const mime = (doc.attachment?.mimeType ?? '').toLowerCase();
  const name = doc.displayName.toLowerCase();
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return PictureAsPdfIcon;
  // HEADER-DOCS-026 follow-up: LibreOffice (odt/ods/odp), CSV and PowerPoint
  if (
    mime.includes('wordprocessingml') ||
    mime === 'application/msword' ||
    mime === 'application/vnd.oasis.opendocument.text' ||
    /\.(docx?|odt)$/.test(name)
  ) {
    return ArticleIcon;
  }
  // Checked before the generic text/* rule so text/csv gets the table icon
  if (
    mime.includes('spreadsheetml') ||
    mime === 'application/vnd.ms-excel' ||
    mime === 'application/vnd.oasis.opendocument.spreadsheet' ||
    mime === 'text/csv' ||
    /\.(xlsx?|ods|csv)$/.test(name)
  ) {
    return TableChartIcon;
  }
  if (
    mime.includes('presentationml') ||
    mime === 'application/vnd.ms-powerpoint' ||
    mime === 'application/vnd.oasis.opendocument.presentation' ||
    /\.(pptx?|odp)$/.test(name)
  ) {
    return SlideshowIcon;
  }
  if (mime.startsWith('image/') || /\.(png|jpe?g|heic|heif|webp)$/.test(name)) return ImageIcon;
  if (mime.startsWith('text/') || /\.(txt|md)$/.test(name)) return TextSnippetIcon;
  return InsertDriveFileIcon;
}
